/**
 * Run converter — converts InlineIR nodes to ProseMirror text nodes with marks.
 *
 * This is a mechanical mapping from resolved IR properties to ProseMirror marks.
 * No OOXML knowledge needed here.
 */

import type { PMNode, PMMark } from "../../ooxmlParser/types";
import type { InlineIR, TextIR } from "../irTypes";
import { halfPtToPt } from "./unitConversion";
import { canonicalizeFontFamily } from "../../fontMap";

/**
 * Build the editor-side footnoteId for an imported note reference.
 * Footnotes and endnotes have independent w:id namespaces, so the kind is
 * baked into the id ("f" vs "e").
 */
export function docxNoteId(
  noteKind: "footnote" | "endnote",
  docxId: string
): string {
  return `fn-docx-${noteKind === "endnote" ? "e" : "f"}-${docxId}`;
}

/**
 * Convert an array of InlineIR nodes to ProseMirror nodes.
 * Handles hyperlink grouping (hyperlinkStart/End → link marks on contained text).
 */
export function convertInlinesToPMNodes(inlines: InlineIR[]): PMNode[] {
  const nodes: PMNode[] = [];
  let activeLinkHref: string | null = null;

  for (const inline of inlines) {
    switch (inline.kind) {
      case "text": {
        const marks = textIRToMarks(inline);
        if (activeLinkHref) {
          marks.push({ type: "link", attrs: { href: activeLinkHref } });
        }
        const textNode: PMNode = { type: "text", text: inline.text };
        if (marks.length > 0) textNode.marks = marks;
        nodes.push(textNode);
        break;
      }

      case "hardBreak":
        nodes.push({ type: "hardBreak" });
        break;

      case "pageBreak":
        nodes.push({ type: "pageBreak" });
        break;

      case "tab": {
        // Render tab as tab character (same as V1)
        const tabNode: PMNode = { type: "text", text: "\t" };
        if (activeLinkHref) {
          tabNode.marks = [{ type: "link", attrs: { href: activeLinkHref } }];
        }
        nodes.push(tabNode);
        break;
      }

      case "symbol": {
        const char = String.fromCharCode(parseInt(inline.charCode, 16));
        const symNode: PMNode = { type: "text", text: char };
        if (activeLinkHref) {
          symNode.marks = [{ type: "link", attrs: { href: activeLinkHref } }];
        }
        nodes.push(symNode);
        break;
      }

      case "inlineMath":
        nodes.push({
          type: "inlineMath",
          attrs: { latex: inline.latex },
        });
        break;

      case "blockMathPlaceholder":
        // Placeholder — resolved in irToProseMirror's resolveMathBlocks pass
        nodes.push({
          type: "_mathBlock",
          attrs: { latex: inline.latex },
        });
        break;

      case "imagePlaceholder":
        // Placeholder — resolved asynchronously in imageResolver
        nodes.push({
          type: inline.imageType === "drawing" ? "_drawing" : "_pict",
          attrs: { _element: inline.element },
        });
        break;

      case "footnoteRef":
        nodes.push({
          type: "footnoteRef",
          attrs: {
            footnoteId: docxNoteId(inline.noteKind, inline.docxId),
            kind: inline.noteKind,
          },
        });
        break;

      case "hyperlinkStart":
        activeLinkHref = inline.href;
        break;

      case "hyperlinkEnd":
        activeLinkHref = null;
        break;

      case "passthroughInline":
        // Pre-built PMNode from V1 delegation
        nodes.push(inline.pmNode);
        break;
    }
  }

  return nodes;
}

/**
 * Convert a TextIR's resolved properties to ProseMirror marks.
 * This matches V1's runPropertiesToMarks + mergeTextStyleMarks behavior.
 */
function textIRToMarks(text: TextIR): PMMark[] {
  const marks: PMMark[] = [];

  if (text.bold) marks.push({ type: "bold" });
  if (text.italic) marks.push({ type: "italic" });
  if (text.underline) marks.push({ type: "underline" });
  if (text.strike) marks.push({ type: "strike" });
  if (text.subscript) marks.push({ type: "subscript" });
  if (text.superscript) marks.push({ type: "superscript" });

  // Collect textStyle attrs
  const textStyleAttrs: Record<string, unknown> = {};

  if (text.color) {
    textStyleAttrs.color = `#${text.color}`;
  }
  if (text.highlight) {
    textStyleAttrs.backgroundColor = text.highlight;
  }
  if (text.fontSizeHalfPt !== undefined) {
    textStyleAttrs.fontSize = `${halfPtToPt(text.fontSizeHalfPt)}pt`;
  }
  if (text.fontFamily) {
    // Canonicalize Word font names to the toolbar's preset stacks so the
    // dropdown recognizes them ("Calibri Light" → "Calibri, Candara,
    // sans-serif"). Unknown fonts keep their raw name — rendering fidelity
    // wins, and the dropdown shows them via a dynamic option.
    textStyleAttrs.fontFamily =
      canonicalizeFontFamily(text.fontFamily) ?? text.fontFamily;
  }

  // Merge all textStyle attrs into a single mark (TipTap expects one textStyle mark)
  if (Object.keys(textStyleAttrs).length > 0) {
    marks.push({ type: "textStyle", attrs: textStyleAttrs });
  }

  return marks;
}
